import { useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { publicacaoService } from '../../publicacoes/publicacaoService';
import type { ArquivoDocumento, CategoriaDocumento, DocumentoEditavel, Publicacao } from '../../publicacoes/publicacao.types';
import { PUBLICACOES_QUERY_KEYS, usePessoaLogada } from '../../publicacoes/usePublicacoes';
import { deInputData } from '../../publicacoes/editor/publicacaoEditor';
import type { FormPublicacao } from '../../publicacoes/editor/publicacaoEditor';
import { arquivoDaEscolha, escolhaCompleta } from './ArquivoDocumentoCampo';
import type { EscolhaArquivo } from './ArquivoDocumentoCampo';

/** Editor de documento: os campos de ciência/agendamento reaproveitam o FormPublicacao dos posts. */
export interface FormDocumento {
  base: FormPublicacao;
  categoria: CategoriaDocumento | '';
  escolha: EscolhaArquivo;
  anunciarNoInicio: boolean;
}

export type AcaoDocumento = 'rascunho' | 'publicar' | 'salvar';

function errosDocumento(form: FormDocumento, existente: Publicacao | null, publicando: boolean): string[] {
  const erros: string[] = [];
  const b = form.base;
  if (!b.titulo.trim()) erros.push('Informe o título.');
  if (publicando) {
    if (!form.categoria) erros.push('Escolha a categoria.');
    if (!b.resumo.trim()) erros.push('Escreva a descrição.');
    if (b.agendar) {
      if (!b.publicarEm) erros.push('Informe quando publicar.');
      else if (new Date(b.publicarEm).getTime() <= Date.now()) erros.push('A data de agendamento precisa ser no futuro.');
    }
  }
  if (!escolhaCompleta(form.escolha, existente?.arquivo)) erros.push(form.escolha.modo === 'link' ? 'Informe um link válido (https://...).' : 'Escolha o arquivo.');
  if (b.exigeCiencia) {
    if (b.publico === 'projetos' && b.projetoIds.length === 0) erros.push('Escolha ao menos um projeto para a ciência.');
    if (b.publico === 'pessoas' && b.pessoaIds.length === 0) erros.push('Escolha ao menos uma pessoa para a ciência.');
    if (b.comPrazo && !b.prazo) erros.push('Informe o prazo da ciência.');
  }
  return erros;
}

/** Salvar documento: cria (se novo) → arquivo → público da ciência → grava → publica/agenda. */
export function useSalvarDocumento() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { autor } = usePessoaLogada();
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState<string[]>([]);

  /** `idNovo`: id já reservado pro documento novo (o arquivo subiu nessa pasta). */
  const salvar = async (form: FormDocumento, existente: Publicacao | null, acao: AcaoDocumento, idNovo: string): Promise<string | null> => {
    if (!autor) return null;
    const validacao = errosDocumento(form, existente, acao === 'publicar' || existente?.status === 'publicada');
    setErros(validacao);
    if (validacao.length > 0) return null;

    setSalvando(true);
    try {
      const b = form.base;
      const prazo = b.exigeCiencia && b.comPrazo ? deInputData(b.prazo) : null;
      const montar = (arquivo: ArquivoDocumento, alvoIds: string[]): DocumentoEditavel => ({
        titulo: b.titulo.trim(),
        resumo: b.resumo.trim(),
        categoria: (form.categoria || 'outros') as CategoriaDocumento,
        arquivo,
        anexos: b.anexos,
        anunciarNoInicio: form.anunciarNoInicio,
        ciencia: {
          exige: b.exigeCiencia,
          publico: b.publico,
          projetoIds: b.exigeCiencia && b.publico === 'projetos' ? b.projetoIds : [],
          alvoIds: b.exigeCiencia && b.publico !== 'todos' ? alvoIds : [],
          prazo: prazo ? Timestamp.fromDate(prazo) : null,
        },
      });

      const arquivo = arquivoDaEscolha(form.escolha, existente?.arquivo);
      const id = existente?.id ?? (await publicacaoService.criarDocumento(montar(arquivo, []), autor, idNovo));
      if (existente?.arquivo?.path && existente.arquivo.path !== arquivo.path) {
        await publicacaoService.removerArquivo(existente.arquivo.path);
      }

      let alvoIds: string[] = [];
      if (b.exigeCiencia && b.publico === 'pessoas') alvoIds = b.pessoaIds;
      if (b.exigeCiencia && b.publico === 'projetos') {
        alvoIds = (await publicacaoService.resolverPublicoPorProjeto(b.projetoIds)).map((c) => c.id);
      }
      await publicacaoService.salvarDocumento(id, montar(arquivo, alvoIds), autor);
      await Promise.all(b.pathsRemovidos.map((path) => publicacaoService.removerArquivo(path)));

      if (acao === 'publicar') {
        await publicacaoService.publicar(id, b.agendar ? new Date(b.publicarEm) : null, autor);
        toast.success(b.agendar ? 'Documento agendado.' : 'Documento publicado.');
      } else if (acao === 'rascunho') {
        if (existente?.status === 'agendada') await publicacaoService.voltarParaRascunho(id, autor);
        toast.success('Rascunho salvo.');
      } else {
        toast.success('Alterações salvas.');
      }
      return id;
    } catch (error) {
      toast.error(`Erro ao salvar: ${(error as { message?: string })?.message ?? 'erro desconhecido'}`);
      return null;
    } finally {
      setSalvando(false);
      queryClient.invalidateQueries({ queryKey: PUBLICACOES_QUERY_KEYS.all });
    }
  };

  return { salvar, salvando, erros, limparErros: () => setErros([]) };
}
